import React, { useMemo, useState, useRef, useEffect } from "react";
import { Download as DownloadIcon, Upload as UploadIcon, X } from 'lucide-react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table';
import { useNavigate } from "react-router-dom"
import SelectList from "../../components/buyer/SelectList";
import axiosInstance, { portInUseForAPI } from "../../api/axiosInstance";
import Loading from "../../components/Loading";
import PopupBanner from "../../components/PopupBanner";
import { CalculateTableArray } from "../../utils/Utils";
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useTranslation } from 'react-i18next'

const BulkPOManagement = () => {
  const { t } = useTranslation()
  const {
    BulkUpload,
    Home,
    DownloadChangeRequestWithTypeOfRequests,
    SelectAChangeRequestType,
    Download,
    UploadYourResponseToOpenChangeRequests,
    ClickToUploadOrDragAndDrop,
    SupportedFile,
    Upload,
    UploadHistory,
    SelectStartDate,
    SelectEndDate,
    Filter,
    Page,
    GoTo,
    Show,
    FileSuccessfullyDownloaded,
    FileUploadedSuccessfully,
    UploadError,
    QuickGuide,
    NoDataAvailable,
    UploadedBy
  } = t('uploadHistory')

  const {
    overlay,
    close,
    title,
    subtitle,
    stepsTitle,
    step1_title,
    step1_detail1,
    step1_detail2,
    step2_title,
    step2_detail1,
    step2_detail2,
    step3_title,
    step3_detail1,
    step3_detail2,
    step4_title,
    step4_detail1,
    step4_detail2,
    step5_title,
    step5_detail1,
    step5_detail2,
    tipsTitle,
    tip1,
    tip2,
    tip3,
    footer
  } = t("quickGuide")

  function formatText(text) {

    const parts = text.split("__bold__");
    return parts.map((part, i) =>
      i % 2 === 1 ? <strong key={i}>{part}</strong> : part
    );

  }

  const user = JSON.parse(localStorage.getItem('userData'))
  const navigate = useNavigate()
  const [sorting, setSorting] = useState([])
  const [loading, setLoading] = useState(false)
  const [showCreateRequestModal, setshowCreateRequestModal] = useState(false)
  const [successmsg, setSuccessMsg] = useState("")

  const [uploadHistoryDetails, setUploadHistoryDetails] = useState([])
  // Dummy data
  const data = useMemo(
    () =>
      uploadHistoryDetails
  );
  const headers = {
    SRNO: t('uploadHistory.SrNo'),
    UPLOADFILENAME: t('uploadHistory.UploadFileName'),
    STATUS: t('uploadHistory.Status'),
    TOTALCOUNT: t('uploadHistory.TotalCount'),
    VALIDCOUNT: t('uploadHistory.ValidCount'),
    INVALIDCOUNT: t('uploadHistory.InvalidCount'),
    UPDATEDAT: t('uploadHistory.UpdatedAt'),
    UPLOADEDBY: t("uploadHistory.UploadedBy"),
    NOACTIONTAKENCOUNT:t("uploadHistory.NoActionTaken")
  }
  // Table columns
  const columns = useMemo(
    () => [
      {
        header: headers.SRNO,
        accessorKey: 'uploadId',
        size: 60,
        minSize: 50,
        maxSize: 80,
        cell: ({ row }) => row.index + 1
      },
      {
        header: headers.UPLOADFILENAME,
        accessorKey: 'fileName',
        size: 350,
        minSize: 340,
        maxSize: 380
      },
      {
        accessorKey: "status",
        header: headers.STATUS,
        cell: ({ row }) => {
          const status = row.getValue("status");

          const statusStyles = {
            PROCESSED: "bg-green-100 text-green-700",
            VALIDATED: "bg-yellow-100 text-yellow-700",
            FAILED: "bg-red-100 text-red-700",
            UPLOADED: "bg-blue-100 text-blue-700",
          };

          return (
            <span
              className={`px-2 py-0.5 rounded-full  text-[10px] ${statusStyles[status]}`}
            >
              {status}
            </span>
          );
        },
      },
      {
        header: headers.TOTALCOUNT,
        accessorKey: 'totalRecords',
      },
      {
        header: headers.VALIDCOUNT,
        accessorKey: 'validCount',
        cell: ({ row }) => {
          const validRecordCount = row.getValue("validCount");
          const uploadId = row.getValue("uploadId");
          const vendorId = row.original.vendorId;
          return (
            <span
              className={"text-green-700 cursor-pointer underline"}
              onClick={() => navigate(`/vendor/bulk-upload/valid-records/${uploadId}/${vendorId}`)}
            >
              {validRecordCount}
            </span>
          );
        },
      },
      {
        header: headers.INVALIDCOUNT,
        accessorKey: 'invalidCount',
        cell: ({ row }) => {
          const inValidRecordCount = row.getValue("invalidCount");
          const uploadId = row.getValue("uploadId")

          const vendorId = row.original.vendorId;
          return (
            <span
              className={"text-red-700 cursor-pointer underline"}
              onClick={() => navigate(`/vendor/bulk-upload/invalid-records/${uploadId}/${vendorId}`)}
            >
              {inValidRecordCount}
            </span>
          );
        },
      },
      {
        header: headers.NOACTIONTAKENCOUNT,
        accessorKey: 'noActionCount',
        cell: ({ row }) => {
          const noActionCount = row.getValue("noActionCount");
          const uploadId = row.getValue("uploadId");
          const vendorId = row.original.vendorId;
          return (
            <span
              className={"text-blue-700 cursor-pointer underline"}
              onClick={() => navigate(`/vendor/bulk-upload/no-action/${uploadId}/${vendorId}`)}
            >
              {noActionCount}
            </span>
          );
        },
      },
      {
        header: headers.UPDATEDAT,
        accessorKey: 'lastUpdated',
        cell: ({ row }) => {
          const date = row.getValue("lastUpdated");

          const formattedWithTime = new Date(date).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          });
          return (
            formattedWithTime
          );
        },
      },
      {
        header: headers.UPLOADEDBY,
        accessorKey: 'uploadedByName',
        size: 250,
        minSize: 240,
        maxSize: 280
      },
    ],
    [headers]
  );

  // React Table instance
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });


  // list of requestTypes
  const list = [
    { "label": "All", "value": "All" },
    { "label": "Confirm", "value": "Confirm" },
    // { "label": "Booking", "value": "Booking" }
    { "label": "Defer", "value": "Defer" },
    // { "value": "Delay", "label": "Delay" }
    { "label": "Divert", "value": "Divert" },
    { "label": "Expedite", "value": "Expedite" },
    { "label": "Reconfirm", "value": "Reconfirm" },
    // { "label": "Urgent", "value": "Urgent" }
    { "label": "Open", "value": "Open" }
  ]
  const [addType, setType] = useState("")

  const handleAddType = (select) => {
    if (select === null) setType("")
    else setType(select?.value)
  }
  // const user = JSON.parse(localStorage.getItem('userData'))
  const handleDownload = () => {
    setLoading(true)
    axiosInstance
      .get(`${process.env.REACT_APP_API_DOMAIN}:${portInUseForAPI}/api/download?userId=${user?.email}&vendorNumber=${user?.supplierId}&crType=${addType === 'All' ? '' : addType.toUpperCase()}`, {
        responseType: "blob", //for file downloads only
      })
      .then((response) => {
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;

        const name = user?.supplierName;
        const timestamp = Date.now();
        const fileName = `${name}${timestamp}.xlsx`;

        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        window.URL.revokeObjectURL(url);
        setLoading(false)
        setshowCreateRequestModal(true)
        const filesName = name + timestamp + ".xlsx"
        setSuccessMsg(
          <>
            {FileSuccessfullyDownloaded}: <span style={{ color: 'green', fontWeight: 'bold' }}>{filesName}</span>
          </>
        );
        setType("")
      })
      .catch(async (error) => {
        let errorMessage = ""
        try {
          if (!(error.response.data instanceof Blob)) {
            throw new Error("Input is not a Blob");
          }
          const text = await error.response.data.text();   // Convert Blob → text
          errorMessage = JSON.parse(text)?.message          // Try parsing JSON

        } catch (err) {
          // If parsing fails, return raw text instead of throwing
          try {
            const text = await error.response.data.text();
            errorMessage = "An error encountered while processing the file"
          } catch {
            errorMessage = "An error encountered while processing the file"
          }
        }
        setLoading(false)
        setshowCreateRequestModal(true)
        const selectedLang = localStorage.getItem("language")
        const errmsg = selectedLang === 'mn' ? UploadValidationZh[errorMessage] : errorMessage
        setSuccessMsg(
          <>
            <span style={{ color: 'red', fontWeight: 'bold' }}>{errmsg}</span>
          </>
        );

        setType("")
      }

      );
  }
  function formatDate(date) {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  //history API Integration
  const getUploadHistoryDetails = (start, end) => {
    setLoading(true)
    const startDate = formatDate(start)
    const endDate = formatDate(end)
    const URL = `/api/bulk-upload/${user?.supplierId}/uploadhistory${(start && end) ? `?startDate=${startDate}&endDate=${endDate}` : ""}`
    axiosInstance
      .get(`${process.env.REACT_APP_API_DOMAIN}:${portInUseForAPI}${URL}`)
      .then((res) => {
        if (res?.data?.length > 0) res?.data.sort((a, b) => new Date(b.lastUpdated) - new Date(a.lastUpdated));
        setUploadHistoryDetails(res?.data)
        setLoading(false)
      }).catch((error) => {
        console.error(error)
      })
  }

  useEffect(() => {
    getUploadHistoryDetails()
  }, [])



  // upload drag and drop functionality
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const UploadValidationZh = {
    "File already uploaded with the same name": "已上传相同名称的文件",
    "File is empty or not provided": "文件为空或未提供",
    "Invalid file type. Only .xlsx files are supported": "文件类型无效，仅支持 .xlsx 文件",
    "File size exceeds allowed limit of 5 MB": "文件大小超出允许的 5 MB 限制",
    "Header row missing in sheet:": "工作表中缺少表头行：",
    "Signature cell not found in sheet:": "工作表中未找到签名单元格：",
    "Signature is empty or null in sheet:": "工作表中的签名为空或无效：",
    "Vendor ID mismatch in sheet:": "工作表中的供应商 ID 不匹配：",
    "User ID mismatch in sheet:": "工作表中的用户 ID 不匹配：",
    "Invalid or tampered file: signature mismatch in sheet": "文件无效或被篡改：签名不匹配",
    "Data saved successfully but UiPath queue failed": "数据已保存，但 UiPath 队列失败",
    "Upload operation timed out. Please try with smaller files or contact support.": "上传操作超时。请尝试较小的文件或联系支持人员。",
    "Records are already processed.": "记录已处理。",
    "No records found for the given criteria.": "未找到符合条件的记录。",
    "Unsupported request type": "不支持的请求类型",
    "Template not found for given type": "未找到指定类型的模板",
    "Records are already processed.": "记录已处理。",
    "Invalid Excel file format for resource": "资源的 Excel 文件格式无效",
    "File not found in resources": "在资源中未找到文件",
    "An error encountered while processing the file": "处理文件时遇到错误"
  };

  const validateAndUploadFile = (selectedFile, e) => {
    // Allowed Excel extensions
    const allowedExtensions = [".xls", ".xlsx"];
    const fileName = selectedFile.name.toLowerCase();
    const isExcel =
      allowedExtensions.some((ext) => fileName.endsWith(ext)) ||
      selectedFile.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      selectedFile.type === "application/vnd.ms-excel"; // old .xls format

    if (!isExcel) {
      setshowCreateRequestModal(true)
      setSuccessMsg(
        <>
          <span style={{ color: 'red', fontWeight: 'bold' }}>{UploadError}(.xls or .xlsx).</span>
        </>
      );
      e.target.value = ""; // reset input
      setFile(null);
      return;
    }

    // Valid Excel file
    else {
      setLoading(true)
      setFile(selectedFile);
      const displayName = localStorage.getItem("displayName")
      const formData = new FormData();
      // const user = JSON.parse(localStorage.getItem('userData'))
      formData.append("file", selectedFile);
      formData.append("vendorId", user?.supplierId);
      formData.append("uploadedByEmail", user?.email);
      formData.append("uploadedByName", displayName)

      axiosInstance.post(`${process.env.REACT_APP_API_DOMAIN}:${portInUseForAPI}/api/bulk-upload/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }).then((res) => {
        setshowCreateRequestModal(true)
        setSuccessMsg(<>
          <span style={{ color: 'green', fontWeight: 'bold' }}>{FileUploadedSuccessfully}</span>
        </>)
        setFile(null)
        setLoading(false)
        setFile(null)
        getUploadHistoryDetails()

      }).catch((err) => {
        setshowCreateRequestModal(true)
        const selectedLnag = localStorage.getItem("language")
        const errmsg = selectedLnag === "mn" ? UploadValidationZh[err?.response?.data?.message] : err?.response?.data?.message
        setSuccessMsg(<>
          <span style={{ color: 'red', fontWeight: 'bold' }}>{errmsg}</span>
        </>)
        setLoading(false)
        setFile(null)
      })
    }
  }

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const selectedFile = e.dataTransfer.files[0]
    validateAndUploadFile(selectedFile, e)
  };


  const removeFile = () => {
    setFile(null);
  };

  const fileInputRef = useRef(null);

  const handleUploadButtonClick = (e) => {
    e.preventDefault(); // prevent form submission
    fileInputRef.current.click(); // open file explorer
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    validateAndUploadFile(selectedFile, e)
  };

  // closing the banner
  const closePopUpModal = () => {
    setshowCreateRequestModal(false)
  }


  // date picker
  // const datePickerRef = useRef(null)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)

  useEffect(() => {
    if (!startDate && !endDate)
      getUploadHistoryDetails()
  }, [startDate, endDate])

  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const togglePanel = () => setIsPanelOpen(!isPanelOpen);

  return (
    <div class="p-2 font-pop">
      {!loading ? <>

        <p className="text-xl mb-4 flex justify-between items-center">
          {BulkUpload}
          <span
            className="text-[12px] text-blue-600 hover:underline cursor-pointer font-semibold"
            onClick={togglePanel}
          >
            {QuickGuide}
          </span>
        </p>

        <div className='w-full text-xs flex justify-start items-center gap-1 mb-4 font-medium text-gray-600'>
          <p className='flex gap-1 items-center justify-center'>
            <span
              className='hover:underline cursor-pointer'
              onClick={() => navigate('/vendor')}
            >
              {Home}
            </span>
            <span>{'>'}</span>
          </p>
          <p className='flex gap-1 items-center justify-center'>
            <span>{BulkUpload}</span>
          </p>
        </div>
        <div className="bg-white rounded mb-5">
          <div className="flex w-full gap-6">

            {/* First Card - Download */}
            <div className="flex-1 border border-gray-300 rounded-md shadow-md shadow-indigo-500/50 p-4">
              <div className="text-center space-y-4">
                <label className="block mb-2 font-medium text-center text-sm">
                  {DownloadChangeRequestWithTypeOfRequests}
                </label>

                <div className=" space-y-4 w-full">
                  <SelectList
                    List={list}
                    handleChangeData={handleAddType}
                    defaultName={addType}
                    placeholder={SelectAChangeRequestType}
                    isClearable={true}
                    className="w-full h-12"
                  />

                  <button
                    onClick={handleDownload}
                    disabled={!addType}
                    className={`${addType ? 'cursor-pointer bg-[#0059A8]' : 'cursor-not-allowed bg-[#4e88b8]'}
              w-full py-2 rounded-md text-white transition flex items-center justify-center`}
                  >
                    <span>{Download}</span>
                    <svg
                      className="w-6 h-6 ml-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 13V4M7 14H5a1 1 0 0 0-1 1v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4a1 1 0 0 0-1-1h-2m-1-5-4 5-4-5m9 8h.01"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Second Card - Upload */}
            <div className="flex-1 border border-gray-300 rounded-md shadow-md shadow-indigo-500/50 p-4">
              <div className="text-center space-y-4">
                <label className="block mb-2 font-medium text-center text-sm">
                  {UploadYourResponseToOpenChangeRequests}
                </label>

                <div className="flex flex-col items-center space-y-4 w-full">
                  <div
                    className={`flex items-center justify-center w-full ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                      }`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('fileInput')?.click()}
                  >
                    <label
                      htmlFor="dropzone-file"
                      className="flex flex-col p-5 items-center justify-center w-full h-24 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                    >

                      <div className="flex flex-col items-center justify-center">
                        <svg
                          className="w-8 h-8 m-1 text-gray-500"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 20 16"
                        >
                          <path
                            stroke="currentColor"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                          />
                        </svg>
                        <p className="mb-1 text-sm text-gray-500">
                          <span className="font-semibold">{ClickToUploadOrDragAndDrop}</span>
                        </p>
                        <p className="text-xs text-gray-500 mb-2">{SupportedFile} : xlsx</p>
                      </div>
                      <input
                        id="dropzone-file"
                        type="file"
                        accept=".xlsx"
                        name="upload"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                    </label>
                  </div>
                  <input type="file" id="fileUpload" class="hidden" accept=".xlsx"
                    onChange={handleFileChange} ref={fileInputRef} />
                  <label for="fileUpload" className="w-full">
                    <button
                      onClick={handleUploadButtonClick}
                      className="cursor-pointer bg-[#0059A8] w-full py-2 rounded-md text-white transition flex items-center justify-center"
                    >
                      <span>{Upload}</span>
                      <svg
                        className="w-6 h-6 ml-2 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 20 16"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                        />
                      </svg>
                    </button>
                  </label>
                </div>
              </div>
            </div>

          </div>
        </div>


        <p class="text-sm font-semibold text-gray-800 mb-4" >{UploadHistory}</p>
        <div className="flex justify-end items-center gap-1 mb-2 text-xs">
          <DatePicker
            selected={startDate}
            onChange={(selectedDate) => { setStartDate(selectedDate); }}
            className='p-1 border rounded'
            // ref={datePickerRef}
            calendarStartDay={1}
            isClearable={true}
            dateFormat="MM/dd/yyyy"
            placeholderText={SelectStartDate}
            maxDate={endDate ? endDate : new Date()}
            popperPlacement="top-start"
          />
          <DatePicker
            selected={endDate}
            onChange={(selectedDate) => { setEndDate(selectedDate); }}
            className='p-1  border rounded'
            // ref={datePickerRef}
            isClearable={true}
            calendarStartDay={1}
            dateFormat="MM/dd/yyyy"
            placeholderText={SelectEndDate}
            minDate={startDate && startDate}
            maxDate={new Date()}
            popperPlacement="top-start"

          />
          <button
            className={`${startDate && endDate ? 'cursor-pointer bg-[#0059A8]' : 'cursor-not-allowed bg-[#4e88b8]'}
              w-[100px] h-[25px] py-1 rounded-md text-white transition flex items-center justify-center`}
            onClick={() => getUploadHistoryDetails(startDate, endDate)}
            disabled={!(startDate && endDate)}
          >
            {Filter}
          </button>
          <button
            className="border rounded p-1"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {'<<'}
          </button>
          <button
            className="border rounded p-1"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {'<'}
          </button>
          <button
            className="border rounded p-1"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {'>'}
          </button>
          <button
            className="border rounded p-1"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {'>>'}
          </button>

          <span className="min-w-max flex items-center gap-1">
            {Page}{" "}
            <strong>
              {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </strong>
          </span>

          <span className="min-w-max flex items-center gap-1">
            | {GoTo}
            <input
              type="number"
              value={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0
                table.setPageIndex(page)
              }}
              className="border rounded px-1 py-0.5 w-12 text-xs"
            />
          </span>

          <select
            className="border rounded px-1 py-0.5 text-xs"
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value))
            }}
          >
            {CalculateTableArray(table?.getFilteredRowModel()?.rows?.length).map((pageSize) => (<option key={pageSize} value={pageSize}> {Show} {pageSize} </option>))}
          </select>
        </div>

        <div className="w-full" >
          <div className="w-full overflow-x-auto">
            <table className="text-xs border border-gray-300 w-full">
              <thead className="bg-[#e0ecfc]">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="divide-x-[1px] divide-gray-300">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="font-medium px-2 py-2 text-center whitespace-nowrap overflow-hidden text-ellipsis"
                        style={{ width: header.getSize() }}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            {...{
                              className: header.column.getCanSort()
                                ? 'cursor-pointer select-none'
                                : '',
                              onClick: header.column.getToggleSortingHandler()
                            }}
                          >
                            <div className='flex justify-center h-5'>
                              {' '}
                              {/* Container with Flexbox */}
                              {/* <div className='text-center'> */}
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {/* </div> */}
                              &nbsp;&nbsp;
                              <div>
                                {{
                                  asc: (
                                    <svg
                                      className='w-3 h-4 text-gray-500'
                                      // className="w-4 h-5 text-gray-500" // Increased size with w-4 and h-5
                                      aria-hidden='true'
                                      xmlns='http://www.w3.org/2000/svg'
                                      fill='none'
                                      viewBox='0 0 10 14'
                                    // viewBox="0 0 14 20" // Adjusted viewBox for larger arrow
                                    >
                                      <path
                                        stroke='currentColor'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M5 13V1m0 0L1 5m4-4 4 4'
                                      // d="M1 5h12m0 0-4 4m4-4L9 1"
                                      />
                                    </svg>
                                  ),
                                  desc: (
                                    <svg
                                      className='w-3 h-4 text-gray-500'
                                      // className="w-4 h-5 text-gray-500" // Increased size with w-4 and h-5
                                      aria-hidden='true'
                                      xmlns='http://www.w3.org/2000/svg'
                                      fill='none'
                                      viewBox='0 0 10 14'
                                    // viewBox="0 0 14 20" // Adjusted viewBox for larger arrow
                                    >
                                      <path
                                        stroke='currentColor'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M5 1v12m0 0 4-4m-4 4L1 9'
                                      // d="M13 5H1m0 0 4 4m-4-4 3-3m0 6V1m0 0 3 3m-3-3 3 3"
                                      />
                                    </svg>
                                  )
                                }[header.column.getIsSorted()] ?? (
                                    <svg
                                      className='w-3 h-4 text-gray-500'
                                      aria-hidden='true'
                                      xmlns='http://www.w3.org/2000/svg'
                                      fill='none'
                                      viewBox='0 0 16 20'
                                    >
                                      <path
                                        stroke='currentColor'
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M4 6v13m0 0 3-3m-3 3-3-3m11-2V1m0 0L9 4m3-3 3 3'
                                      // d="M4 6h10m0 0-3-3m3 3-3 3m1 8H1m0 0 3-3m-3 3 3 3"
                                      />
                                    </svg>
                                  )}
                              </div>
                            </div>
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              {table.getRowModel().rows.length > 0 ? <tbody>
                {table.getRowModel().rows.map((row, index) => (
                  <tr
                    key={row.id}
                    className={`divide-x-[1px] divide-gray-300 ${index % 2 === 0 ? 'bg-[#ffffff]' : 'bg-[#ecedf2]'
                      }`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-2 py-2 text-center"
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody> : <tr>
                <td colspan="8" class="text-center py-4 text-black-600">
                  {NoDataAvailable}
                </td>
              </tr>}
            </table>
          </div>
        </div>
      </> : <Loading />}

      <PopupBanner
        isOpen={showCreateRequestModal}
        message={successmsg}
        onCancel={closePopUpModal}

      />
      {/* Panel */}
      {isPanelOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-40 cursor-pointer"
            onClick={togglePanel}
          />

          {/* Side Panel */}

          <aside className="fixed top-0 right-0 w-[600px] h-full bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-y-auto transition-transform duration-300 ease-in-out font-sans">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-red-600 text-xl font-bold z-50"
              onClick={togglePanel}
              aria-label={t.close}
            >
              ✕
            </button>

            <main className="max-w-4xl mx-auto sm:p-8">
              <header className="mb-8">
                <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
                <p className="mt-3 text-slate-600">{subtitle}</p>
              </header>

              <div className="grid grid-cols-1 gap-6">
                <section>
                  <h1 className="text-xl font-semibold text-slate-900 mb-4">{stepsTitle}</h1>

                  <ol className="space-y-4">
                    {/* Step 1 */}
                    <li className="flex gap-4 items-start bg-white p-2 rounded-lg shadow-sm">
                      <div className="flex-shrink-0">
                        <span className="flex items-center justify-center h-10 w-10 rounded-full bg-indigo-600 text-white font-semibold">
                          1
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-slate-900">{step1_title}</h3>
                        <ul className="mt-4 mb-2 ml-4">
                          <li className="mt-1 text-sm text-slate-600 list-disc">{formatText(step1_detail1)}</li>
                          <li className="mt-1 text-sm text-slate-600 list-disc">{formatText(step1_detail2)}</li>
                        </ul>
                      </div>
                    </li>

                    {/* Step 2 */}
                    <li className="flex gap-4 items-start bg-white p-2 rounded-lg shadow-sm">
                      <div className="flex-shrink-0">
                        <span className="flex items-center justify-center h-10 w-10 rounded-full bg-indigo-600 text-white font-semibold">
                          2
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-slate-900">{step2_title}</h3>
                        <ul className="mt-4 mb-2 ml-4">
                          <li className="mt-1 text-sm text-slate-600 list-disc">{formatText(step2_detail1)}</li>
                          <li className="mt-1 text-sm text-slate-600 list-disc">{formatText(step2_detail2)}</li>
                        </ul>
                      </div>
                    </li>

                    {/* Step 3 */}
                    <li className="flex gap-4 items-start bg-white p-2 rounded-lg shadow-sm">
                      <div className="flex-shrink-0">
                        <span className="flex items-center justify-center h-10 w-10 rounded-full bg-indigo-600 text-white font-semibold">
                          3
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-slate-900">{step3_title}</h3>
                        <ul className="mt-4 mb-2 ml-4">
                          <li className="mt-1 text-sm text-slate-600 list-disc">{formatText(step3_detail1)}</li>
                          <li className="mt-1 text-sm text-slate-600 list-disc">{formatText(step3_detail2)}</li>
                        </ul>
                      </div>
                    </li>

                    {/* Step 4 */}
                    <li className="flex gap-4 items-start bg-white p-2 rounded-lg shadow-sm">
                      <div className="flex-shrink-0">
                        <span className="flex items-center justify-center h-10 w-10 rounded-full bg-indigo-600 text-white font-semibold">
                          4
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-slate-900">{step4_title}</h3>
                        <ul className="mt-4 mb-2 ml-4">
                          <li className="mt-1 text-sm text-slate-600 list-disc">{formatText(step4_detail1)}</li>
                          <li className="mt-1 text-sm text-slate-600 list-disc">{formatText(step4_detail2)}</li>
                        </ul>
                      </div>
                    </li>

                    {/* Step 5 */}
                    <li className="flex gap-4 items-start bg-white p-2 rounded-lg shadow-sm">
                      <div className="flex-shrink-0">
                        <span className="flex items-center justify-center h-10 w-10 rounded-full bg-indigo-600 text-white font-semibold">
                          5
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-medium text-slate-900">{step5_title}</h3>
                        <ul className="mt-4 mb-2 ml-4">
                          <li className="mt-1 text-sm text-slate-600 list-disc">{formatText(step5_detail1)}</li>
                          <li className="mt-1 text-sm text-slate-600 list-disc">{formatText(step5_detail2)}</li>
                        </ul>
                      </div>
                    </li>
                  </ol>

                </section>

                <section>
                  <div className="sticky top-6">
                    <div className="mt-4 p-4 rounded-lg bg-gradient-to-r from-green-50 to-green-100 border border-green-200">
                      <h4 className="text-md font-medium text-green-800">{tipsTitle}</h4>
                      <ul className="mt-2 text-sm text-green-700 space-y-1 list-disc list-inside">
                        <li>{formatText(tip1)}</li>
                        <li>{formatText(tip2)}</li>
                        <li>{formatText(tip3)}</li>
                      </ul>
                    </div>
                  </div>
                </section>
              </div>

              <footer className="mt-8 text-sm text-slate-500">{footer}</footer>
            </main>
          </aside>

        </>
      )}
    </div >
  );
};

export default BulkPOManagement;
